import { useCallback, useEffect, useRef, useState } from 'react'

import { useDispatch } from 'react-redux'

import { fetchIncognitoRequestStatusUniNoticeAction } from 'actions/profile/fetchIncognitoRequestStatusUniNotice'
import { fetchRequestIncognitoStatusAction } from 'actions/profile/fetchRequestIncognitoStatusAction'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

/**
 * Пользователь может выбрать "скрывать фото", т.е. получить статус инкогнито.
 * Просматривающий пользователь может запросить доступ к фото пользователя.
 * При клилке по такому фото(размытому), мы должны показать нотис с инфой о статусе запроса на доступ к скрытому фото.
 */
export const usePhotoClick = (userId: string | number) => {
  const dispatch = useDispatch()

  const {
    isIncognitoStatus,
    isCurrentUserCanSeePhotos,
    incognitoRequestStatus,
  } = useShallowEqualSelector(
    ({
      photoViewerReducer: { isIncognitoStatus, isCurrentUserCanSeePhotos },
      profile: { incognitoRequestStatus },
    }) => ({
      isIncognitoStatus,
      isCurrentUserCanSeePhotos,
      incognitoRequestStatus,
    })
  )

  // Запросим статус запроса к инкогнито
  useEffect(() => {
    if (isIncognitoStatus && !isCurrentUserCanSeePhotos) {
      dispatch(fetchRequestIncognitoStatusAction(userId))
    }
  }, [dispatch, isCurrentUserCanSeePhotos, isIncognitoStatus, userId])

  const [fetching, setFetching] = useState(false)
  const clickedRef = useRef(false)

  const onPhotoClick = useCallback(async () => {
    if (!incognitoRequestStatus) {
      // Данные еще не подгрузились, но мы запомним клик пользователя, чтобы выполнить обработчик позднее
      clickedRef.current = true
      return
    }

    if (fetching) {
      return
    }

    setFetching(true)
    await dispatch(fetchIncognitoRequestStatusUniNoticeAction(userId))
    setFetching(false)
  }, [dispatch, fetching, incognitoRequestStatus, userId])

  // Этот хук обработает отложенное действие по клику
  useEffect(() => {
    if (incognitoRequestStatus && clickedRef.current) {
      onPhotoClick().catch(console.error)
      clickedRef.current = false
    }
  }, [incognitoRequestStatus, onPhotoClick])

  return {
    onPhotoClick,
  }
}
